import { apiService } from 'services/ServiceRegistry';
import navigation from 'services/NavigationService';

/**
 * Our FormHooksModule for Asset Request Form
 * Here we create our logic for submit and parsing form data
 * @type {{onError(*, *, *): function(...[*]=), submitForm(*=, *=): void, getFormData(*): {end_date: *, delivery_contact_phone: (*), cost_centre_id: *, customer_contact_name: *, delivery_special_instructions: *, delivery_contact_name: (*), additional_information: *, account_id: *, project_id: *, customer_site_id: *, delivery_site_id: *, customer_contact_phone: *, rush_asset_details: [{category_id: *, quantity: number, data_plan: *, software: *, displays: *, peripherals: *}], start_date: *}, onSuccess(*, *=, *): function(...[*]=)}}
 */
const FormHooksModule = () => {
  /**
   * Gets form data and parser into correct format for request
   * @param form
   */
  const getFormAssets = (form: Object) => {
    const assets = form.$('rushAssetDetails');

    return assets.map(rushAssetDetails => {
      const categoryId = rushAssetDetails.$('description').value;
      const descQuantity = rushAssetDetails.$('quantity').value;

      const software = rushAssetDetails.$('software').value;
      const softwareArray =
        software.length !== 0 ? software.map(el => ({ id: el, quantity: descQuantity })) : [];

      const dataPlan = rushAssetDetails.$('dataPlan').value;
      const displaysArray = rushAssetDetails.$('displays').value;
      const peripheralsArray = rushAssetDetails.$('peripherals').value;

      return {
        category_id: categoryId || null,
        quantity: descQuantity || null,
        data_plan: dataPlan
          ? [
              {
                id: dataPlan,
                quantity: descQuantity,
              },
            ]
          : [],
        software: softwareArray,
        displays: displaysArray || [],
        peripherals: peripheralsArray || [],
      };
    });
  };

  /**
   * Gets form data
   * @param form
   */
  const getFormData = (form: Object) => {
    const accountId = form.$('chooseAccount.tradingAccount').extra.selectID;
    const projectId = form.$('assignProjects.list').extra.selectID;
    const costCentreId = form.$('assignCostCentre.list').extra.selectID;

    const {
      requiredDates,
      additionalInformation,
      customerDetails,
      deliveryDetails,
    } = form.values();

    const customerSiteId = form.$('customerDetails.site').extra.selectID;
    const customerCompanyId = form.$('customerDetails.company').extra.selectID;
    const { deliveryIsEqual } = customerDetails;
    const deliverySiteId = form.$('deliveryDetails.site').extra.selectID;
    const deliveryCompanyId = form.$('deliveryDetails.company').extra.selectID;

    const assets = getFormAssets(form);

    return {
      account_id: accountId,
      project_id: projectId || null,
      cost_centre_id: costCentreId || null,
      start_date: requiredDates.startDate,
      end_date: requiredDates.endDate,
      additional_information: additionalInformation.details,
      customer_contact_name: customerDetails.name,
      customer_contact_phone: customerDetails.phoneNo,
      customer_company_id: customerCompanyId,
      customer_site_id: customerSiteId,
      use_customer_details_for_delivery: Boolean(deliveryIsEqual),
      delivery_contact_name: deliveryDetails.name,
      delivery_contact_phone: deliveryDetails.phoneNo,
      delivery_company_id: deliveryCompanyId,
      delivery_site_id: deliverySiteId,
      delivery_special_instructions: deliveryDetails.specialInstructions,
      rush_asset_details: assets,
    };
  };

  /**
   * Submit form
   * Gathers and sends form information to the API
   * @param formData
   * @param localState
   */
  const submitForm = (formData, localState, t) => {
    apiService
      .createResource('rush-asset-request/create', formData)
      .then((response: Object) => {
        localState.isLoading = false;
        localState.responseMessage = t('general:messages.save_success');
        setTimeout(() => {
          localState.showResponseMessage = false;
          /*eslint-disable max-nested-callbacks*/
          setTimeout(() => {
            // Snackbar animation 225ms
            navigation.navigateTo(`/sent-requests/${response.sentRequestId}`);
          }, 225);
        }, 1200);
      })
      .catch((err: string) => {
        localState.isLoading = false;
        localState.responseMessage =
          err && err.errors && err.errors.name && err.errors.name.length > 0
            ? err.errors.name[0]
            : err && err.message;
        localState.hasSuccess = false;
        localState.hasError = true;
      });
  };

  return {
    /**
     * onSuccess Closure
     * Returns our hook function
     * Upper scope params are fields, local state & translate object
     * @returns {function(...[*]=)}
     */
    onSuccess(fields: Object, localState: Object, t: Function) {
      return (form: Object) => {
        localState.isLoading = true;
        localState.responseMessage = t('general:messages.saving_form');
        localState.showResponseMessage = true;
        localState.hasError = false;
        localState.hasSuccess = true;

        const data = getFormData(form);

        submitForm(data, localState, t);
      };
    },

    /**
     * onError Closure
     *  * Returns our hook function
     * Upper scope params are fields, local state & translate object
     * @returns {function(...[*]=)}
     */
    onError(fields: Object, localState: Object, t: Function) {
      return (form: Object) => {
        localState.isLoading = false;
        localState.responseMessage = t('general:messages.form_error');
        localState.showResponseMessage = true;
        localState.hasSuccess = false;
        localState.hasError = true;
      };
    },
  };
};

export const onSuccess = FormHooksModule().onSuccess;
export const onError = FormHooksModule().onError;
